// src/components/client/StylistServices.tsx
import React, { useState, useEffect } from 'react';
import TeslaButton from '../common/TeslaButton';
import  stylistService  from '../../services/stylistService';
import { ServiceWithDetails } from '../../types/stylist';
import { formatCurrency } from '../../utils/formatters';

interface StylistServicesProps {
  stylistId: string;
  onSelectService: (serviceId: string, service?: ServiceWithDetails) => void;
}

const StylistServices: React.FC<StylistServicesProps> = ({ 
  stylistId,
  onSelectService
}) => {
  const [services, setServices] = useState<ServiceWithDetails[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  
  useEffect(() => {
    fetchStylistServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stylistId]); // fetchStylistServices is stable
  
  const fetchStylistServices = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const services = await stylistService.getStylistServices(stylistId, { onlyEnabled: true });
      setServices(services);
      
      // Auto-expand the first category if any services exist
      if (services.length > 0) {
        const categories = getUniqueCategories(services);
        if (categories.length > 0) {
          setExpandedCategory(categories[0]);
        }
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching stylist services:', err);
      setError('Failed to load services');
      setServices([]);
      setLoading(false);
    }
  };
  
  // Get unique categories from services
  const getUniqueCategories = (services: ServiceWithDetails[]): string[] => {
    const categories = services.map(service => service.service.category);
    // Use a more compatible approach than spread operator with Set
    return Array.from(new Set(categories));
  };
  
  // Group services by category
  const servicesByCategory = services.reduce((acc: Record<string, ServiceWithDetails[]>, service) => {
    const category = service.service.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(service);
    return acc;
  }, {});
  
  const handleExpandCategory = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };
  
  const handleServiceSelect = (serviceId: string) => {
    const service = services.find(s => s._id === serviceId);
    onSelectService(serviceId, service);
  };
  
  if (loading) {
    return (
      <div className="services__loading">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="message message--error">
        {error}
      </div>
    );
  }
  
  if (services.length === 0) {
    return (
      <div className="message message--info">
        This stylist hasn't added any services yet.
      </div>
    );
  }
  
  return (
    <div className="services">
      <h3 className="services__title">Available Services</h3>
      
      {Object.keys(servicesByCategory).map(category => (
        <div 
          key={category}
          className={`service-category ${expandedCategory === category ? 'expanded' : ''}`}
        >
          <button 
            className="service-category__header" 
            onClick={() => handleExpandCategory(category)}
          >
            <h4 className="service-category__title">
              {category.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
            </h4>
            <span className="service-category__icon">
              {expandedCategory === category ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 15l-6-6-6 6"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9l6 6 6-6"/>
                </svg>
              )}
            </span>
          </button>
          
          {expandedCategory === category && (
            <div className="service-category__content">
              <ul className="service-list">
                {servicesByCategory[category].map((service, index) => (
                  <li 
                    key={service._id} 
                    className={`service-item ${index < servicesByCategory[category].length - 1 ? 'with-divider' : ''}`}
                  >
                    <div className="service-item__header">
                      <h5 className="service-item__name">
                        {service.service.name}
                      </h5>
                      <span className="service-item__price">
                        {formatCurrency(service.service.basePrice?.min ?? 0)}
                      </span>
                    </div>
                    
                    <p className="service-item__description">
                      {service.service.description?.short || 'No description available.'}
                    </p>
                    
                    <div className="service-item__duration">
                      <svg 
                        width="16" 
                        height="16" 
                        viewBox="0 0 24 24" 
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="service-item__duration-icon"
                      >
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      <span className="service-item__duration-text">
                        {service.service.baseDuration.min === service.service.baseDuration.max
                          ? `${service.service.baseDuration.min} min`
                          : `${service.service.baseDuration.min} - ${service.service.baseDuration.max} min`}
                      </span>
                    </div>
                    
                    <div className="service-item__action">
                      <TeslaButton
                        variant="secondary"
                        size="sm"
                        onClick={() => handleServiceSelect(service._id)}
                      >
                        Book This Service
                      </TeslaButton>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default StylistServices;